import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Drawer, EmptyState, Inbox01, Table } from "@opendate-app/setlist";

import { errorText, toastError } from "../../../shared/toastHelper";

const styles = {
  link: { color: "var(--sl-text-brand)", textDecoration: "none" },
  muted: { color: "var(--sl-text-tertiary)" },
  message: { minWidth: 220, maxWidth: 360, whiteSpace: "pre-wrap", color: "var(--sl-text-secondary)" },
  footer: { display: "flex", justifyContent: "center", marginTop: 16 },
};

const formatReceived = (value) => new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const formatDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const blank = <span style={styles.muted}>—</span>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const displayable = (value) => typeof value === "string" || Number.isFinite(value) || (Array.isArray(value) && value.every((item) => typeof item === "string"));

const formatAnswer = (question, stored) => {
  const value = stored?.value;
  if (!displayable(value) || value === "" || (Array.isArray(value) && value.length === 0)) return blank;
  if (Array.isArray(value)) return value.join(", ");
  const type = stored.type || question.type;
  if (type === "date" && typeof value === "string" && ISO_DATE.test(value)) return formatDate(value);
  if (type === "number") return Number(value).toLocaleString();
  if (type === "long_text") return <div style={styles.message}>{value}</div>;
  return value;
};

const removedQuestions = (questions, submissions) => {
  const current = new Set(questions.map((q) => q.key));
  const removed = new Map();
  submissions.forEach((submission) => {
    Object.entries(submission.answers || {}).forEach(([key, stored]) => {
      if (current.has(key) || removed.has(key)) return;
      removed.set(key, { key, label: `${stored?.label ?? key} (removed)`, type: stored?.type });
    });
  });
  return [...removed.values()];
};

const answerColumn = (q) => ({
  key: q.key,
  header: q.label,
  align: q.type === "number" ? "right" : undefined,
  render: (s) => formatAnswer(q, s.answers?.[q.key]),
});

const SubmissionsDrawer = ({ form, teamId, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [loading, setLoading] = useState(false);

  const request = useRef(null);

  const load = async (pageNumber) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    try {
      const { data } = await axios.get(`/teams/${teamId}/signup_forms/${form.id}/submissions.json`, { params: { page: pageNumber }, signal: controller.signal });
      if (controller.signal.aborted) return;
      setSubmissions((previous) => {
        if (pageNumber === 1) return data.submissions;
        const seen = new Set(previous.map((submission) => submission.id));
        return [...previous, ...data.submissions.filter((submission) => !seen.has(submission.id))];
      });
      setNextPage(data.next_page);
    } catch (error) {
      if (!controller.signal.aborted) toastError(errorText(error, "Could not load submissions, please try again."));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    setSubmissions([]);
    setNextPage(null);
    setLoading(false);
    if (form) load(1);
    return () => request.current?.abort();
  }, [form?.id]);

  const questions = Array.isArray(form?.config?.questions) ? form.config.questions : [];

  const columns = [
    { key: "received", header: "Received", render: (s) => formatReceived(s.created_at) },
    { key: "name", header: "Name", primary: true, render: (s) => (s.name ? <a style={styles.link} href={`/teams/${teamId}/crm/fans/${s.fan_id}`}>{s.name}</a> : blank) },
    { key: "email", header: "Email", render: (s) => <a style={styles.link} href={`mailto:${s.email}`}>{s.email}</a> },
    { key: "phone", header: "Phone", render: (s) => (s.phone ? <a style={styles.link} href={`tel:${s.phone}`}>{s.phone}</a> : blank) },
    ...questions.map(answerColumn),
    ...removedQuestions(questions, submissions).map(answerColumn),
  ];

  return (
    <Drawer
      open={Boolean(form)}
      onClose={onClose}
      side="right"
      size="xl"
      backdrop
      title={form ? `Submissions · ${form.name}` : "Submissions"}
      subtitle="Newest first. Reply by email or phone to follow up."
    >
      {form && (
        <>
          <Table
            columns={columns}
            data={submissions}
            getRowKey={(s) => s.id}
            loading={loading && submissions.length === 0}
            empty={(
              <EmptyState
                icon={Inbox01}
                size="sm"
                title="No submissions yet"
                description="When someone fills out this form, it shows up here."
              />
            )}
          />
          {nextPage && (
            <div style={styles.footer}>
              <Button variant="secondary" disabled={loading} onClick={() => load(nextPage)}>Load more</Button>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default SubmissionsDrawer;
